
import { BreadCrumb } from "primereact/breadcrumb";

import { MenuItem, MenuItemCommandEvent, MenuItemOptions } from "primereact/menuitem";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BreadcrumbState } from "../../redux/breadcrumb-slice";
import { AppState } from "../../redux/store";
import { BreadcrumbInfo } from "../../types";

import { ModulesState } from "../../redux/module-slice";
import { useIcon, useTabTitle } from "../../hooks";
import { FabrixIcon } from "../../common/icons";

export interface BreadCrumbsProps {
  style?: React.CSSProperties
}


/**
 * Renders the PrimeReact breadcrumb navigation wired to Fabrix routing state.
 * @param props Optional style overrides for the breadcrumb container.
 * @returns Breadcrumb trail reflecting the current module hierarchy.
 */
export default function BreadCrumbs(props: BreadCrumbsProps) {

  const breadcrumbState = useSelector<AppState>(state => state.breadcrumbs) as BreadcrumbState;
  const [_actions, setActions] = useState<BreadcrumbInfo[]>(breadcrumbState.items);
  const _moduleState = useSelector<AppState>(state => state.modules) as ModulesState;
  const icon = useIcon();
  const setTitle = useTabTitle();

  const navigate = useNavigate();

  /**
   * Handles breadcrumb item selection by navigating to the associated path.
   * @param e PrimeReact menu command event triggered by the breadcrumb click.
   */
  const doNavigation = (e: MenuItemCommandEvent) => {
    let item = e.item as BreadcrumbInfo;
    navigate(item.path);
  }

  const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey
    ) {
      return;
    }

    event.preventDefault();
    navigate(path);
  };

  /**
   * Removes leading and trailing slashes to normalize module paths.
   * @param text Module or breadcrumb path.
   * @returns Path string without surrounding slashes.
   */
  const cleanSlashes = (text: string) => {
    return text.replace(/^\/+|\/+$/g, '');
  }


  /**
   * Builds a breadcrumb template rendered as a Fabrix icon span.
   * @param item Breadcrumb menu item.
   * @param options Template options provided by PrimeReact.
   * @returns JSX anchor linking to the breadcrumb path.
   */
  const renderIcon = (item: MenuItem) => {
    if (!item.icon || typeof item.icon !== "string") {
      return null;
    }

    const iconValue = item.icon;
    const iconIsClassName =
      iconValue.includes(" ") ||
      iconValue.startsWith("fbx-") ||
      iconValue.startsWith("fa") ||
      iconValue.startsWith("mdi-") ||
      iconValue.startsWith("pi-") ||
      iconValue.startsWith("ri-");

    return iconIsClassName ? (
      <span className={`fbx-breadcrumb-icon ${iconValue}`} aria-hidden="true" />
    ) : (
      <span className="fbx-breadcrumb-icon material-icons" aria-hidden="true">{iconValue}</span>
    );
  };

  const buildBreadcrumbItem = (item: MenuItem, options: MenuItemOptions) => {

    let bItem = item as BreadcrumbInfo;

    return (
      <a
        href={bItem.path}
        className={options.className}
        data-pc-section="action"
        onClick={(event) => handleLinkClick(event, bItem.path)}
      >
        {renderIcon(item)}
        {item.label ? <span data-pc-section="label" className="p-menuitem-text">{item.label}</span> : null}
      </a>
    )
  }

  /**
   * Recursively assigns navigation handlers, URLs, and icon templates to breadcrumb items.
   * @param crumbs Breadcrumb entries sourced from state.
   * @returns Augmented breadcrumb list ready for PrimeReact consumption.
   */
  const linkProc = (crumbs: BreadcrumbInfo[]) => {
    let items = [...crumbs].map(x => ({ ...x })) as BreadcrumbInfo[];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.items && item.items.length > 0) {
        item.items = linkProc(item.items as BreadcrumbInfo[]);
      } else {
        item.command = doNavigation;
      }

      // Ensure each breadcrumb has a proper URL so browser features like
      // "open in new tab" use the correct target instead of the current page.
      item.url = item.path;

      let module = _moduleState.modules.find(x => cleanSlashes(x.path) === cleanSlashes(item.path))
      if (module)
        // if (module.image) {
        //   item.icon = module.image;
        //   item.template = buildFabrixImage
        //} 
        if (module.icon) {
          item.icon = module.icon
        }

      item.template = buildBreadcrumbItem;
    }
    return items;

  }

  useEffect(() => {
    let crumbs = breadcrumbState.items;
    crumbs = linkProc(crumbs);

    setActions(crumbs);
  }, [breadcrumbState.items])

  useEffect(() => {
    let labels = _actions.map(x => x.label).join(" > ")
    if (labels)
      setTitle(labels);
  }, [_actions])

  let home: BreadcrumbInfo | undefined = { icon: icon(FabrixIcon.home), id:"home", command: doNavigation, label: "", path: '/', segment: "", url: "/", template: buildBreadcrumbItem }



  return (
    <BreadCrumb model={_actions} home={home} style={props.style} className="fbx-breadcrumb">
    </BreadCrumb>
  )
}
